<script setup>
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePublishedArticlesQuota } from 'dashboard/composables/usePublishedArticlesQuota';
import { usePortalsQuota } from 'dashboard/composables/usePortalsQuota';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import FeaturePaywall from 'dashboard/routes/dashboard/settings/components/FeaturePaywall.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { accountId } = useAccount();
const { articlesQuotaExhausted } = usePublishedArticlesQuota();
const { portalsQuotaExhausted } = usePortalsQuota();

const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const currentAccountId = useMapGetter('getCurrentAccountId');
const getAccount = useMapGetter('accounts/getAccount');

const isAccountReady = computed(() =>
  Boolean(getAccount.value(currentAccountId.value)?.id)
);
const isBehindAPaywall = computed(
  () =>
    isAccountReady.value &&
    !isFeatureEnabledonAccount.value(
      currentAccountId.value,
      FEATURE_FLAGS.HELP_CENTER
    )
);

const portals = computed(() => store.getters['portals/allPortals']);

const selectedPortal = computed(() => {
  const slug =
    route.params.portalSlug || uiSettings.value.last_active_portal_slug;
  if (slug) return store.getters['portals/portalBySlug'](slug);
  return portals.value[0];
});

const defaultPortalLocale = computed(() =>
  selectedPortal.value ? selectedPortal.value.meta?.default_locale : ''
);
const selectedLocaleInPortal = computed(
  () => route.params.locale || defaultPortalLocale.value
);

const selectedPortalSlug = computed(() =>
  selectedPortal.value ? selectedPortal.value.slug : ''
);

const showArticlesQuotaBanner = computed(
  () => !isBehindAPaywall.value && articlesQuotaExhausted.value
);
const showPortalsQuotaBanner = computed(
  () => !isBehindAPaywall.value && portalsQuotaExhausted.value
);

const goToBilling = () => {
  router.push({
    name: 'billing_settings_index',
    params: { accountId: accountId.value },
  });
};

const fetchPortalAndItsCategories = async () => {
  await store.dispatch('portals/index');
  const selectedPortalParam = {
    portalSlug: selectedPortalSlug.value,
    locale: selectedLocaleInPortal.value,
  };
  store.dispatch('portals/show', selectedPortalParam);
  store.dispatch('categories/index', selectedPortalParam);
  store.dispatch('agents/get');
};

watch(
  [isAccountReady, isBehindAPaywall],
  ([ready, paywalled]) => {
    if (!ready || paywalled) return;
    fetchPortalAndItsCategories();
  },
  { immediate: true }
);

watch(
  () => route.params.portalSlug,
  newSlug => {
    if (newSlug && newSlug !== uiSettings.value.last_active_portal_slug) {
      updateUISettings({
        last_active_portal_slug: newSlug,
        last_active_locale_code: selectedLocaleInPortal.value,
      });
    }
  }
);
</script>

<template>
  <div
    v-if="isBehindAPaywall"
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-surface-1"
  >
    <FeaturePaywall feature-prefix="HELP_CENTER" />
  </div>
  <div v-else-if="isAccountReady" class="flex w-full h-full min-h-0">
    <section
      class="flex flex-col flex-1 h-full px-0 overflow-hidden bg-n-surface-1"
    >
      <div
        v-if="showPortalsQuotaBanner || showArticlesQuotaBanner"
        class="flex flex-col gap-2 px-6 pt-3 shrink-0"
      >
        <Banner
          v-if="showPortalsQuotaBanner"
          color="amber"
          :action-label="t('HELP_CENTER.UPGRADE_PAGE.BUTTON.UPGRADE')"
          @action="goToBilling"
        >
          {{ t('HELP_CENTER.QUOTA.PORTALS_EXHAUSTED') }}
        </Banner>
        <Banner
          v-if="showArticlesQuotaBanner"
          color="amber"
          :action-label="t('HELP_CENTER.UPGRADE_PAGE.BUTTON.UPGRADE')"
          @action="goToBilling"
        >
          {{ t('HELP_CENTER.QUOTA.EXHAUSTED') }}
        </Banner>
      </div>
      <div class="flex flex-1 min-h-0">
        <router-view />
      </div>
    </section>
  </div>
</template>
